import { useState } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { formatDate } from '../utils/helpers'

export default function AdminLayout() {
    const { user, logout } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()

    const isPublicCmsActive = ['/cms/home', '/cms/about', '/cms/contact', '/cms/coverage', '/cms/faq'].includes(location.pathname)
    const isLegalCmsActive = ['/cms/terms', '/cms/privacy', '/cms/affiliate-terms', '/cms/security', '/cms/sle'].includes(location.pathname)
    const isBlogActive = location.pathname.startsWith('/cms/blogs')

    const [publicOpen, setPublicOpen] = useState(isPublicCmsActive)
    const [legalOpen, setLegalOpen] = useState(isLegalCmsActive)

    const handleLogout = () => {
        logout()
        navigate('/login')
    }

    const navItems = [
        { to: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
        { to: '/crm', label: 'CRM', icon: CrmIcon },
        { to: '/users', label: 'Users', icon: UsersIcon },
        { to: '/subscriptions', label: 'Subscriptions', icon: SubscriptionsIcon },
        // { to: '/plans', label: 'Plans', icon: PlansIcon },
        { to: '/tenders', label: 'Tenders', icon: TendersIcon },
        { to: '/ingestion', label: 'Ingestion Logs', icon: IngestionIcon },
        { to: '/alerts', label: 'Alerts', icon: AlertsIcon },
        { to: '/feedback', label: 'Feedback', icon: FeedbackIcon },
        { to: '/service-type-alternative-names', label: 'Service Type Names', icon: ServiceTypeNamesIcon },
        { to: '/commissioning-authorities', label: 'Commissioning Authorities', icon: CommissioningAuthoritiesIcon },
    ]

    const cmsPublicItems = [
        { to: '/cms/home', label: 'Home', icon: CmsPageIcon },
        { to: '/cms/about', label: 'About Us', icon: CmsPageIcon },
        { to: '/cms/contact', label: 'Contact', icon: CmsPageIcon },
        { to: '/cms/coverage', label: 'Coverage', icon: CmsPageIcon },
        { to: '/cms/faq', label: 'FAQ', icon: CmsPageIcon },
    ]

    const cmsLegalItems = [
        { to: '/cms/terms', label: 'Terms & Conditions', icon: CmsPageIcon },
        { to: '/cms/privacy', label: 'Privacy & Data', icon: CmsPageIcon },
        // { to: '/cms/affiliate-terms', label: 'Affiliate Terms', icon: CmsPageIcon },
        // { to: '/cms/security', label: 'Security', icon: CmsPageIcon },
        // { to: '/cms/sle', label: 'Service Level', icon: CmsPageIcon },
    ]

    const allNavItems = [...navItems, ...cmsPublicItems, ...cmsLegalItems, { to: '/cms/settings', label: 'Site Settings' }, { to: '/cms/exclusion-keywords', label: 'Exclusion Keywords' }]

    const pageTitle = location.pathname.startsWith('/crm/organisations/')
        ? 'Organisation'
        : location.pathname.startsWith('/users/') || location.pathname.startsWith('/user-profile/')
        ? 'User Profile'
        : location.pathname.startsWith('/cms/blogs/')
            ? 'Blog Editor'
            : location.pathname === '/cms/blogs'
                ? 'Insights'
                : allNavItems.find(item => item.to === location.pathname)?.label || 'Dashboard'

    return (
        <div className="flex h-screen bg-admin-bg overflow-hidden font-sans">
            {/* SIDEBAR - Professional Dark Theme (Brand Aligned) */}
            <aside className="w-64 bg-brand-navy flex flex-col shadow-xl z-20 flex-shrink-0 transition-all duration-300 border-r border-brand-navy-dark">
                {/* Logo Area */}
                <div className="h-16 flex items-center px-6 border-b border-white/10 bg-brand-navy">
                    <img src="/logo.png" alt="TC" className="h-10 w-auto" />
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
                    <div className="px-3 mb-3 text-[10px] font-bold text-blue-200 uppercase tracking-widest opacity-80">
                        Overview
                    </div>
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 group relative ${isActive
                                    ? 'bg-white/10 text-white shadow-md border border-white/10'
                                    : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                                }`
                            }
                        >
                            <item.icon className={`w-5 h-5 transition-colors ${({ isActive }) => isActive ? 'text-white' : 'text-blue-300 group-hover:text-white'}`} />
                            {item.label}
                        </NavLink>
                    ))}

                    {/* CMS Section Label */}
                    <div className="px-3 pt-5 pb-2 text-[10px] font-bold text-blue-200 uppercase tracking-widest opacity-80">
                        CMS
                    </div>

                    {/* Public Pages Tree Node */}
                    <div>
                        <button
                            onClick={() => setPublicOpen(o => !o)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 group ${isPublicCmsActive
                                ? 'bg-white/10 text-white border border-white/10'
                                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            <CmsGroupIcon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                            <span className="flex-1 text-left">Public Pages</span>
                            <ChevronIcon className={`w-4 h-4 text-blue-300 transition-transform duration-200 ${publicOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {publicOpen && (
                            <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                                {cmsPublicItems.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        className={({ isActive }) =>
                                            `flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 group ${isActive
                                                ? 'bg-white/10 text-white shadow-md border border-white/10'
                                                : 'text-blue-100/70 hover:bg-white/5 hover:text-white'
                                            }`
                                        }
                                    >
                                        <item.icon className="w-4 h-4 text-blue-300 flex-shrink-0" />
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Blog */}
                    <NavLink
                        to="/cms/blogs"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 group ${isBlogActive || isActive
                                ? 'bg-white/10 text-white shadow-md border border-white/10'
                                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                            }`
                        }
                    >
                        <BlogNavIcon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                        Insights
                    </NavLink>

                    {/* Exclusion Keywords */}
                    <NavLink
                        to="/cms/exclusion-keywords"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 group ${isActive
                                ? 'bg-white/10 text-white shadow-md border border-white/10'
                                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                            }`
                        }
                    >
                        <CmsPageIcon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                        Exclusion Keywords
                    </NavLink>

                    {/* Legal Pages Tree Node */}
                    <div>
                        <button
                            onClick={() => setLegalOpen(o => !o)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 group ${isLegalCmsActive
                                ? 'bg-white/10 text-white border border-white/10'
                                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                                }`}
                        >
                            <CmsLegalIcon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                            <span className="flex-1 text-left">Legal Pages</span>
                            <ChevronIcon className={`w-4 h-4 text-blue-300 transition-transform duration-200 ${legalOpen ? 'rotate-90' : ''}`} />
                        </button>
                        {legalOpen && (
                            <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                                {cmsLegalItems.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        className={({ isActive }) =>
                                            `flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 group ${isActive
                                                ? 'bg-white/10 text-white shadow-md border border-white/10'
                                                : 'text-blue-100/70 hover:bg-white/5 hover:text-white'
                                            }`
                                        }
                                    >
                                        <item.icon className="w-4 h-4 text-blue-300 flex-shrink-0" />
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Site Settings */}
                    <NavLink
                        to="/cms/settings"
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 group ${isActive
                                ? 'bg-white/10 text-white shadow-md border border-white/10'
                                : 'text-blue-100/80 hover:bg-white/5 hover:text-white'
                            }`
                        }
                    >
                        <CmsPageIcon className="w-5 h-5 text-blue-300 flex-shrink-0" />
                        Site Settings
                    </NavLink>
                </nav>

                {/* Footer / Sign Out */}
                <div className="p-4 border-t border-white/10 bg-brand-navy-dark/30">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold text-white bg-white/10 border border-white/10 rounded hover:bg-white/20 hover:text-white transition-all shadow-sm"
                    >
                        <LogoutIcon className="w-4 h-4" />
                        Sign Out
                    </button>
                    {/* <p className="mt-4 text-[10px] text-center text-blue-200/50 font-mono">v1.2.0-stable</p> */}
                </div>
            </aside>

            {/* MAIN CONTENT WRAPPER */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-admin-bg">
                {/* HEADER */}
                <header className="h-16 bg-white border-b border-admin-border flex items-center justify-between px-8 shadow-sm z-10">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-bold text-admin-heading tracking-tight">{pageTitle}</h1>
                    </div>

                    {/* Global Actions & Profile */}
                    <div className="flex items-center gap-6">

                        {/* Status Pills */}
                        <div className="hidden md:flex items-center gap-4">
                            <div className="flex items-center gap-1.5 px-3 py-1 bg-status-success-bg text-status-success rounded-full border border-status-success/20 shadow-sm">
                                <div className="w-1.5 h-1.5 rounded-full bg-status-success animate-pulse"></div>
                                <span className="text-xs font-bold">System Live</span>
                            </div>
                            <div className="text-xs text-admin-muted font-mono font-medium">{formatDate(new Date())}</div>
                        </div>

                        <div className="h-8 w-px bg-admin-border"></div>

                        {/* User Profile */}
                        <div className="flex items-center gap-3 pl-2">
                            <div className="flex flex-col items-end">
                                <span className="text-sm font-bold text-admin-text leading-tight">{user?.name || 'Administrator'}</span>
                                <span className="text-[10px] font-bold text-admin-muted uppercase tracking-wider">{user?.organisation || 'TenderCentriX HQ'}</span>
                            </div>
                            <div className="relative group cursor-pointer">
                                <div className="w-10 h-10 rounded-full bg-brand-navy flex items-center justify-center text-sm font-bold text-white shadow-md ring-2 ring-slate-100 group-hover:ring-brand-navy/30 transition-all">
                                    {user?.name?.charAt(0) || 'A'}
                                </div>
                                <div className="absolute top-0 right-0 w-3 h-3 bg-status-success border-2 border-white rounded-full"></div>
                            </div>
                        </div>

                    </div>
                </header>

                {/* SCROLLABLE VIEW */}
                <main className="flex-1 overflow-y-auto p-8 bg-slate-50 relative">
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    )
}

// ============================================================
// ICONS (Professional Stroke Set)
// ============================================================
function DashboardIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
    )
}

function UsersIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
    )
}

function CrmIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5" />
        </svg>
    )
}

function SubscriptionsIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
    )
}

function PlansIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .672-3 1.5S10.343 11 12 11s3 .672 3 1.5-1.343 1.5-3 1.5m0-6V6m0 1c1.11 0 2.08.402 2.599 1M12 15v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    )
}

function TendersIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
    )
}

function IngestionIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
    )
}

function AlertsIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
    )
}

function ServiceTypeNamesIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5.586a1 1 0 01.707.293l7.414 7.414a1 1 0 010 1.414l-7.586 7.586a1 1 0 01-1.414 0L4.293 12.293A1 1 0 014 11.586V5a2 2 0 012-2z" />
        </svg>
    )
}

function CommissioningAuthoritiesIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6M9 11h.01M15 11h.01" />
        </svg>
    )
}

function LogoutIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
    )
}

function ResourcesIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
    )
}

function CmsPageIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
    )
}

function CmsGroupIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
        </svg>
    )
}

function CmsLegalIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
        </svg>
    )
}

function BlogNavIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
    )
}

function FeedbackIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
        </svg>
    )
}

function ChevronIcon({ className }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    )
}
